<script>
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import Dropdown from '../../../components/Dropdown.vue';
import { resolveVariablePath } from '../../helpers/variablePathResolver';

export default {
  components: {
    Dropdown,
    ButtonNext,
    IconNext,
  },
  props: {
    showDropdown: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    event: {
      type: [Object, Event],
      default: () => {},
    },
    variables: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: String,
      default: '',
    },
    filterByPrefix: {
      type: String,
      default: '',
    },
    // When set (e.g. 'attachment'), only leaves of this type are selectable;
    // wrappers stay visible so nested objects remain drillable.
    filterByLeafType: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'add'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      toLocale,
    };
  },
  data() {
    return {
      search: '',
      breadcrumbs: [],
    };
  },
  computed: {
    variableItems() {
      const items = this.variables;
      const filteredItems = items.filter(item => !item.hidden);
      return filteredItems.map(item => {
        const dropdownLabel = this.toLocale(item.label, item.name);
        const dropdownValue = item.name;
        const children = this.getChildItems(item);
        const prefix = item.prefix;
        return { ...item, dropdownLabel, dropdownValue, children, prefix };
      });
    },
    wrapperItems() {
      const breadcrumbs = this.breadcrumbs;
      const breadcrumb = breadcrumbs[breadcrumbs.length - 1];
      if (breadcrumb) return this.getChildItems(breadcrumb);

      return [];
    },
    dropdownItems() {
      return this.breadcrumbs.length ? this.wrapperItems : this.variableItems;
    },
    preFilteredDropdownItems() {
      return this.dropdownItems
        .filter(item => {
          if (!this.filterByPrefix) return true;
          const prefix = item.prefix ?? '%';
          return prefix === this.filterByPrefix;
        })
        .filter(item => {
          if (!this.filterByLeafType) return true;
          return this.hasMatchingLeaf(item);
        });
    },
    filteredDropdownItems() {
      const searchQuery = this.search.toLowerCase();
      return this.preFilteredDropdownItems
        .filter(item => {
          const searchIndices = [
            item.dropdownLabel,
            ...item.children.map(({ dropdownLabel }) => dropdownLabel),
          ]
            .join()
            .toLowerCase();
          return searchIndices.includes(searchQuery);
        })
        .filter((item, index, items) => {
          const itemDropdownValue = `${item.prefix ?? '%'}${item.dropdownValue}`;
          const isDuplicate =
            index ===
            items.findIndex(i => {
              const iDropdownValue = `${i.prefix ?? '%'}${i.dropdownValue}`;
              return iDropdownValue === itemDropdownValue;
            });
          return isDuplicate;
        })
        .map(item => {
          const active = item.dropdownValue === this.selected;
          return { ...item, active };
        });
    },
    variableSections() {
      const actionLevel = this.filteredDropdownItems.filter(
        item => item.prefix === '#'
      );
      const normal = this.filteredDropdownItems.filter(
        item =>
          (!item.prefix || item.prefix === '%') &&
          !item.dropdownValue.startsWith('__')
      );
      const system = this.filteredDropdownItems.filter(
        item =>
          (!item.prefix || item.prefix === '%') &&
          item.dropdownValue.startsWith('__')
      );

      const sections = [];
      if (actionLevel.length > 0) {
        sections.push({
          label:
            'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.BREADCRUMBS.ACTION_LEVEL_VARIABLES',
          items: actionLevel,
        });
      }
      if (normal.length > 0) {
        sections.push({
          label:
            'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.BREADCRUMBS.VARIABLES',
          items: normal,
        });
      }
      if (system.length > 0) {
        sections.push({
          label:
            'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.BREADCRUMBS.SYSTEM_VARIABLES',
          items: system,
        });
      }

      // Inside a breadcrumb with no children — show empty section
      // so the breadcrumb navigation is visible.
      if (sections.length === 0 && this.breadcrumbs.length > 0) {
        const prefix = this.breadcrumbs[0].prefix || '%';
        const label =
          prefix === '#'
            ? 'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.BREADCRUMBS.ACTION_LEVEL_VARIABLES'
            : 'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.BREADCRUMBS.VARIABLES';
        sections.push({ label, items: [] });
      }

      return sections;
    },
    noItems() {
      if (!this.showDropdown) return false;
      if (this.preFilteredDropdownItems.length > 0) return false;
      // Don't treat as "no items" when inside a breadcrumb —
      // show empty state instead of closing the dropdown.
      if (this.breadcrumbs.length > 0) return false;
      return true;
    },
    noResult() {
      return this.filteredDropdownItems.length === 0 && this.search !== '';
    },
  },
  watch: {
    showDropdown(current, before) {
      if (current !== before) {
        if (current) {
          this.focusInput();
          this.onDropdownShow();
        } else {
          this.onDropdownClose();
        }
      }
    },
    noItems: {
      handler(value) {
        if (value) {
          this.$emit('close');
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.focusInput();
  },
  methods: {
    focusInput() {
      this.$nextTick(() => {
        if (!this.$refs.searchbar) return;
        this.$refs.searchbar.focus();
      });
    },
    onDropdownShow() {
      this.setInitialPage();
    },
    onDropdownClose() {
      this.setDefaults();
    },
    setDefaults() {
      this.breadcrumbs = [];
      this.search = '';
    },
    setInitialPage() {
      const breadcrumbsPath = this.selected.split('.').slice(0, -1);
      if (breadcrumbsPath.length === 0) return;
      // Walk the path through wrapper children (custom objects can nest custom
      // objects), keeping whatever prefix of the path resolves.
      const { chain } = resolveVariablePath(
        this.variables,
        breadcrumbsPath.join('.'),
        {
          getCustomObjects:
            this.$store.getters['wgptAutomationRegistry/getCustomObjects'],
          getDynamicOptions:
            this.$store.getters['wgptAutomationRegistry/getDynamicOptions'],
          toLocale: this.toLocale,
        }
      );
      this.breadcrumbs = chain;
    },
    // A leaf matches when its type equals filterByLeafType; a wrapper matches
    // only when some descendant does, so empty drill-downs are hidden.
    hasMatchingLeaf(item) {
      if (!item.wrapper) return item.type === this.filterByLeafType;

      return (item.children || []).some(child => this.hasMatchingLeaf(child));
    },
    getChildItems(variable, seenObjects = []) {
      if (variable.wrapper) {
        const breadcrumbs = this.breadcrumbs;

        if (variable.custom_object) {
          // Nested custom objects could form a reference cycle; don't expand
          // an object that is already open higher up the chain.
          if (seenObjects.includes(variable.custom_object)) return [];
          const nestedSeenObjects = [...seenObjects, variable.custom_object];
          const customObjects =
            this.$store.getters['wgptAutomationRegistry/getCustomObjects'](
              variable.custom_object
            ) || {};
          const items = customObjects.options || [];
          return items.map(item => {
            const dropdownLabel = this.toLocale(item.label, item.name);
            const dropdownValue = [
              ...breadcrumbs.map(({ name }) => name),
              item.name,
            ].join('.');
            const children = this.getChildItems(item, nestedSeenObjects);
            const prefix = variable.prefix;
            return { ...item, dropdownLabel, dropdownValue, children, prefix };
          });
        }
        if (variable.model) {
          const dynamicOptions =
            this.$store.getters['wgptAutomationRegistry/getDynamicOptions'](
              variable.model
            ) || {};
          const items = dynamicOptions.options || [];
          return items.map(item => {
            const dropdownLabel = item.label;
            const dropdownValue = [
              ...breadcrumbs.map(({ name }) => name),
              item.value,
            ].join('.');
            const children = this.getChildItems(item, seenObjects);
            const prefix = variable.prefix;
            return { ...item, dropdownLabel, dropdownValue, children, prefix };
          });
        }
      }

      return [];
    },
    onClick(variable) {
      if (variable.wrapper) {
        this.breadcrumbs.push(variable);
        this.search = '';
        return;
      }

      this.$emit('add', variable.dropdownValue, variable.prefix);
    },
    clearBreadcrumbs() {
      this.breadcrumbs = [];
    },
    selectBreadcrumb({ name: breadcrumbName }) {
      const breadcrumbIndex = this.breadcrumbs.findIndex(
        ({ name }) => name === breadcrumbName
      );
      this.breadcrumbs = this.breadcrumbs.slice(0, breadcrumbIndex + 1);
    },
  },
};
</script>

<template>
  <Dropdown
    class="variables-dropdown pointer-events-auto"
    :show-dropdown="showDropdown"
    :event="event"
    anchor="bottom"
    :gap="4"
    @close="$emit('close')"
  >
    <div class="w-[300px]">
      <div v-if="showSearch" class="relative border-b border-n-strong">
        <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
        <input
          ref="searchbar"
          v-model="search"
          type="search"
          :disabled="preFilteredDropdownItems.length === 0"
          :placeholder="
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.MULTI_SELECTOR.PLACEHOLDER'
            )
          "
          class="reset-base outline-none w-full py-2 pl-10 pr-2 text-sm border-none rounded-t-md bg-n-solid-1 text-slate-900 dark:text-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      <div class="py-1 overflow-auto max-h-60">
        <div class="flex flex-col">
          <template
            v-for="(section, sectionIndex) in variableSections"
            :key="sectionIndex"
          >
            <div
              v-if="sectionIndex > 0"
              class="mx-3 my-1 border-t border-n-weak"
            />
            <div class="px-3 pt-2 pb-1 flex items-center gap-0.5">
              <ButtonNext
                variant="link"
                size="xs"
                color="slate"
                :label="$t(section.label)"
                class="!text-xs !font-semibold uppercase text-n-slate-10"
                @click="clearBreadcrumbs"
              />
              <div
                v-for="(breadcrumb, index) in breadcrumbs"
                :key="index"
                class="breadcrumb overflow-hidden"
              >
                <IconNext
                  icon="i-lucide-chevron-right"
                  class="shrink-0"
                  size="14"
                />
                <ButtonNext
                  variant="link"
                  size="xs"
                  color="slate"
                  class="truncate"
                  :label="toLocale(breadcrumb.label, breadcrumb.name)"
                  @click="selectBreadcrumb(breadcrumb)"
                />
              </div>
            </div>
            <ul
              v-if="section.items.length > 0"
              class="py-0.5 mb-0"
              role="listbox"
            >
              <li
                v-for="option of section.items"
                :key="option.value"
                class="flex items-center justify-between w-full gap-2 px-3 py-1.5 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
                :class="{ 'bg-n-alpha-2': option.active }"
                role="option"
                :aria-selected="option.active"
                @click="onClick(option)"
              >
                <span class="text-n-slate-12 truncate">
                  {{ option.dropdownLabel }}
                </span>
                <span
                  v-if="option.active"
                  class="flex-shrink-0 i-lucide-check size-4 text-n-green-11"
                />
                <span
                  v-else-if="option.wrapper"
                  class="flex-shrink-0 i-lucide-chevron-right size-4 text-n-slate-11"
                />
              </li>
            </ul>
            <p v-else class="px-3 py-2 mb-0 text-sm text-n-slate-11">
              {{
                $t(
                  'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.MULTI_SELECTOR.EMPTY'
                )
              }}
            </p>
          </template>
        </div>
        <ul v-if="noResult" class="py-1 mb-0" role="listbox">
          <li class="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">
            {{
              $t(
                'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.VARIABLES_DROPDOWN.MULTI_SELECTOR.NO_RESULTS'
              )
            }}
          </li>
        </ul>
      </div>
    </div>
  </Dropdown>
</template>

<style lang="scss" scoped>
.breadcrumb {
  @apply flex items-center gap-0.5 text-slate-500 dark:text-slate-500;
}
</style>
